import React, { useCallback } from 'react';
import styled from 'astroturf/react';

import { createUserFollowing } from 'js/social/api/user_following_api';
import { FollowableUser } from 'js/social/shared/types';
import Avatar from 'js/social/shared/Avatar';
import { FollowActionButton } from 'js/social/shared/FollowActionButton';
import { FeedPostsActions } from 'js/social/News/useNewsInteractions';

export type PostEngagementFollowPromptProps = Pick<
  FeedPostsActions,
  'setUserFollowing'
> & {
  user: FollowableUser;
  followSource?: string;
  followSourceDetails?: { [key: string]: string | number | undefined };
};

const Banner = styled.div`
  @import 'stylesheets/design_system/_color.scss';
  @import 'stylesheets/design_system/font';
  @include body__small-bold;

  display: flex;
  padding: 8px;
  align-items: center;
  align-self: stretch;
  gap: 12px;

  background-color: #eef2f9;
  border: 1px solid #cddaef;
  border-radius: 4px;
  box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.08);

  color: $ds-black;
`;

const AvatarMessageContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
`;

const AvatarStyled = styled(Avatar)`
  flex-shrink: 0;
`;

const FollowButton = styled(FollowActionButton)`
  @import 'stylesheets/design_system/_color.scss';

  &:disabled {
    background-color: $ds-gray-light;
    color: $ds-gray-dark;
    cursor: default;
  }
`;

export const PostEngagementFollowPrompt: React.FC<
  PostEngagementFollowPromptProps
> = ({
  user: { id: userId, following, photo, displayName, url },
  setUserFollowing,
  followSource = 'social_post_engagement_prompt',
  followSourceDetails,
}) => {
  const handleFollow = useCallback(async () => {
    await createUserFollowing(userId, followSource, followSourceDetails);
    setUserFollowing(userId, true);
  }, [userId, setUserFollowing, followSourceDetails, followSource]);

  return (
    <Banner>
      <AvatarMessageContainer>
        <AvatarStyled
          className={'clickable'}
          photoUrl={photo}
          fullName={displayName}
          profileUrl={url}
        />
        {`Follow ${displayName} to see more posts like this`}
      </AvatarMessageContainer>
      <FollowButton
        userId={userId}
        follows={following}
        onFollow={handleFollow}
        size="xs"
        variant="primary"
      />
    </Banner>
  );
};
